import { useMemo } from 'react'
import type { ModelData, ConfigurationState, PricingData } from '../types'
import { getFusionPrices, getFusionCornerPrices } from '../data/fusionPrices'
import { getAkuBoxPrice, getThermalHangersData } from '../utils/optionGenerator'

export const usePriceCalculation = (
  model: ModelData | null, 
  config: ConfigurationState
): PricingData => {
  return useMemo(() => {
    if (!model) {
      return {
        basePrice: 0,
        totalPrice: 0,
        breakdown: {
          glassPrice: 0,
          bezelPrice: 0,
          fusionPrice: 0,
          fusionCornerPrice: 0,
          akuPlusPrice: 0,
          nemoThermalProtectionPrice: 0,
          akuBoxPrice: 0,
          easy700Price: 0,
          cmaNPrice: 0,
          cmaSPrice: 0,
          thermalModulesPrice: 0,
          thermalHangersPrice: 0,
          controllerPrice: 0,
          doorSensorPrice: 0,
          handleOnSmallGlassPrice: 0
        }
      }
    }

    // Show price immediately (no restrictions)

    const modelId = (model.id || '').toLowerCase()
    const isNemoModel = modelId.startsWith('nemo') || model.name.toLowerCase().includes('nemo')

    let basePrice = 0
    let totalPrice = 0

    // Helper function to get base price
    const getBasePrice = (variant: string, glass: string): number | null => {
      return model.basePrices[variant]?.[glass] ?? null
    }

    if (model.glassType === 'vis-a-vis') {
      // VIS-A-VIS PRICING - calculate price progressively
      const frontVariant = config['front-variant'] || ''
      const frontGlass = config.glass || 'single'
      const backVariant = config['back-variant'] || ''
      const backGlass = config.glass || 'single'

      let frontBase = 0
      let backBase = 0
      
      // Get front price if variant selected
      if (frontVariant && frontVariant !== '') {
        frontBase = getBasePrice(frontVariant, frontGlass) || 0
      }
      
      // Get back price if variant selected
      if (backVariant && backVariant !== '') {
        backBase = getBasePrice(backVariant, backGlass) || 0
      }
      
      // Use the maximum of both sides (or just one if only one selected)
      basePrice = Math.max(frontBase, backBase)

      // Additional prices for vis-a-vis - only add if option is selected
      const ceramicPrice = config.ceramic ? (model.extraPrices?.ceramic?.[config.ceramic] || 0) : 0
      
      const frontHandlePrice = (config['front-handle'] && config['front-handle'] !== 'not-applicable') 
        ? (model.extraPrices?.handle?.[config['front-handle']] || 0) : 0
      const backHandlePrice = (config['back-handle'] && config['back-handle'] !== 'not-applicable') 
        ? (model.extraPrices?.handle?.[config['back-handle']] || 0) : 0
        
      const frontBezelPrice = (config['front-bezel'] && frontVariant) 
        ? (model.extraPrices?.bezel?.[frontVariant]?.[config['front-bezel']] || 0) : 0
      const backBezelPrice = (config['back-bezel'] && backVariant) 
        ? (model.extraPrices?.bezel?.[backVariant]?.[config['back-bezel']] || 0) : 0

      // FUSION prices - using centralized pricing system
      const fusionVariant = (frontVariant && frontVariant.includes('lift')) ? 'lift' : 'standard'
      const fusionPrices = getFusionPrices(model.id || 'default', fusionVariant)
      const fusionBasicPrice = config.fusionBasic === 'fusion' 
        ? (fusionPrices?.fusion || 0) 
        : 0
      const fusionModulesPrice = config.fusionModules === 'fusion-modules' 
        ? ((fusionPrices?.modules || 0) * parseInt(String(config.fusionModulesCount || 0), 10))
        : 0

      // FUSION CORNER prices - using centralized pricing system
      const fusionCornerPrices = getFusionCornerPrices(model.id || 'default', fusionVariant)
      const fusionCornerBasicPrice = config.fusionCorner === 'fusion-corner' && fusionCornerPrices
        ? (fusionCornerPrices?.fusion || 0) 
        : 0
      const fusionCornerModulesPrice = config.fusionCornerModules === 'fusion-corner-modules' && fusionCornerPrices
        ? ((fusionCornerPrices?.modules || 0) * parseInt(String(config.fusionCornerModulesCount || 0), 10))
        : 0

      // AKU+ price (if available and selected)
      const akuPrice = config.akuPlus === 'aku-plus'
        ? (model.extraPrices?.akuPlus?.['aku-plus'] || 0)
        : 0

      // NEMO thermal protection price
      const nemoThermalProtectionPrice = config.nemoThermalProtection === 'with-protection'
        ? 420
        : 0

      // Accumulation materials prices
      const akuBoxPrice = config.akuBox === 'aku-box'
        ? getAkuBoxPrice(model)
        : 0
      
      const easy700Price = config.easy700 === 'easy-700' && !isNemoModel
        ? 2210
        : 0
      
      const cmaNPrice = config.cmaN === 'cma-n'
        ? 2880
        : 0
      
      const cmaSPrice = config.cmaS === 'cma-s'
        ? 3030
        : 0

      // Thermal modules price (if available and selected)
      const thermalModulesPrice = config.thermalModules && config.thermalModules !== 'none'
        ? (model.extraPrices?.thermalModules?.[config.thermalModules] || 0) * (config.thermalModulesCount || 1)
        : 0

      // Thermal hangers price (if available and selected)
      const thermalHangersData = getThermalHangersData(model)
      const thermalHangersPrice = config.thermalHangers === 'yes' && thermalHangersData
        ? thermalHangersData.price
        : 0

      // Controllers price (if available and selected)
      const controllerPrice = config.controller && config.controller !== 'none'
        ? (model.extraPrices?.controllers?.[config.controller] || 0)
        : 0

      // Door sensor price (if available and selected)
      const doorSensorPrice = config.doorSensor === 'czujnik-drzwiczek'
        ? (model.extraPrices?.controllers?.['czujnik-drzwiczek'] || 0)
        : 0

      // Handle on small glass price is not applicable for vis-a-vis
      const handleOnSmallGlassPrice = 0

      // Calculate glass surcharge (difference from cheapest glass combination)
      const frontVariantPrices = model.basePrices[frontVariant] || {}
      const backVariantPrices = model.basePrices[backVariant] || {}
      const cheapestFrontPrice = frontVariantPrices.single || 0
      const cheapestBackPrice = backVariantPrices.single || 0
      const cheapestCombinationPrice = Math.max(cheapestFrontPrice, cheapestBackPrice)
      const glassPrice = basePrice - cheapestCombinationPrice

      totalPrice = basePrice + ceramicPrice + frontHandlePrice + backHandlePrice + 
                   frontBezelPrice + backBezelPrice + fusionBasicPrice + fusionModulesPrice + 
                   fusionCornerBasicPrice + fusionCornerModulesPrice + akuPrice + nemoThermalProtectionPrice + akuBoxPrice + 
                   easy700Price + cmaNPrice + cmaSPrice + thermalModulesPrice + thermalHangersPrice + controllerPrice + doorSensorPrice + handleOnSmallGlassPrice

      return {
        basePrice,
        totalPrice,
        breakdown: {
          glassPrice,
          bezelPrice: frontBezelPrice + backBezelPrice,
          fusionPrice: fusionBasicPrice + fusionModulesPrice,
          fusionCornerPrice: fusionCornerBasicPrice + fusionCornerModulesPrice,
          akuPlusPrice: akuPrice,
          nemoThermalProtectionPrice,
          akuBoxPrice,
          easy700Price,
          cmaNPrice,
          cmaSPrice,
          thermalModulesPrice,
          thermalHangersPrice,
          controllerPrice,
          doorSensorPrice,
          handleOnSmallGlassPrice
        }
      }
    } else {
      // STANDARD MODEL PRICING - calculate price progressively
      const variant = config.variant || ''
      const glass = config.glass || 'single'
      
      // Get base price if variant selected
      if (variant && variant !== '') {
        basePrice = getBasePrice(variant, glass) || 0
        if (basePrice === 0) {
          // Try with default glass if selected glass doesn't work
          basePrice = getBasePrice(variant, 'single') || 0
        }
      }

      // Additional prices - only add if options are selected
      const ceramicPrice = config.ceramic ? (model.extraPrices?.ceramic?.[config.ceramic] || 0) : 0
      const handlePrice = config.handle ? (model.extraPrices?.handle?.[config.handle] || 0) : 0
      const bezelPrice = (config.bezel && variant) ? (model.extraPrices?.bezel?.[variant]?.[config.bezel] || 0) : 0

      // FUSION prices - using centralized pricing system
      const fusionVariant = (variant && variant.includes('lift')) ? 'lift' : 'standard'
      const fusionPrices = getFusionPrices(model.id || 'default', fusionVariant)
      const fusionBasicPrice = config.fusionBasic === 'fusion' 
        ? (fusionPrices?.fusion || 0) 
        : 0
      const fusionModulesPrice = config.fusionModules === 'fusion-modules' 
        ? ((fusionPrices?.modules || 0) * parseInt(String(config.fusionModulesCount || 0), 10))
        : 0

      // FUSION CORNER prices - using centralized pricing system
      const fusionCornerPrices = getFusionCornerPrices(model.id || 'default', fusionVariant)
      const fusionCornerBasicPrice = config.fusionCorner === 'fusion-corner' && fusionCornerPrices
        ? (fusionCornerPrices?.fusion || 0) 
        : 0
      const fusionCornerModulesPrice = config.fusionCornerModules === 'fusion-corner-modules' && fusionCornerPrices
        ? ((fusionCornerPrices?.modules || 0) * parseInt(String(config.fusionCornerModulesCount || 0), 10))
        : 0

      // AKU+ price (if available)
      const akuPrice = config.akuPlus === 'aku-plus' 
        ? (model.extraPrices?.akuPlus?.['aku-plus'] || 0) 
        : 0

      // NEMO thermal protection price
      const nemoThermalProtectionPrice = config.nemoThermalProtection === 'with-protection'
        ? 420
        : 0

      // Accumulation materials prices
      const akuBoxPrice = config.akuBox === 'aku-box'
        ? getAkuBoxPrice(model)
        : 0
      
      const easy700Price = config.easy700 === 'easy-700' && !isNemoModel
        ? 2210
        : 0
      
      const cmaNPrice = config.cmaN === 'cma-n'
        ? 2880
        : 0
      
      const cmaSPrice = config.cmaS === 'cma-s'
        ? 3030
        : 0

      // Thermal modules price (if available and selected)
      const thermalModulesPrice = config.thermalModules && config.thermalModules !== 'none'
        ? (model.extraPrices?.thermalModules?.[config.thermalModules] || 0) * (config.thermalModulesCount || 1)
        : 0

      // Thermal hangers price (if available and selected)
      const thermalHangersData = getThermalHangersData(model)
      const thermalHangersPrice = config.thermalHangers === 'yes' && thermalHangersData
        ? thermalHangersData.price
        : 0

      // Controllers price (if available and selected)
      const controllerPrice = config.controller && config.controller !== 'none'
        ? (model.extraPrices?.controllers?.[config.controller] || 0)
        : 0

      // Door sensor price (if available and selected)
      const doorSensorPrice = config.doorSensor === 'czujnik-drzwiczek'
        ? (model.extraPrices?.controllers?.['czujnik-drzwiczek'] || 0)
        : 0

      // Handle on small glass price (corner models only)
      const handleOnSmallGlassPrice = (config.handleOnSmallGlass && model.glassType === 'narozna') ? 400 : 0

      // Calculate glass surcharge (difference from cheapest glass option)
      const variantPrices = model.basePrices[variant] || {}
      const cheapestGlassPrice = Math.min(...Object.values(variantPrices).filter(price => typeof price === 'number'))
      const glassPrice = basePrice - cheapestGlassPrice

      totalPrice = basePrice + ceramicPrice + handlePrice + bezelPrice + 
                   fusionBasicPrice + fusionModulesPrice + fusionCornerBasicPrice + fusionCornerModulesPrice + 
                   akuPrice + nemoThermalProtectionPrice + akuBoxPrice + easy700Price + cmaNPrice + cmaSPrice + thermalModulesPrice + thermalHangersPrice + controllerPrice + doorSensorPrice + handleOnSmallGlassPrice

      return {
        basePrice,
        totalPrice,
        breakdown: {
          glassPrice,
          bezelPrice,
          fusionPrice: fusionBasicPrice + fusionModulesPrice,
          fusionCornerPrice: fusionCornerBasicPrice + fusionCornerModulesPrice,
          akuPlusPrice: akuPrice,
          nemoThermalProtectionPrice,
          akuBoxPrice,
          easy700Price,
          cmaNPrice,
          cmaSPrice,
          thermalModulesPrice,
          thermalHangersPrice,
          controllerPrice,
          doorSensorPrice,
          handleOnSmallGlassPrice
        }
      }
    }
  }, [model, config])
}
